import React from 'react';
import pluralize from 'pluralize';
import JSONUtils from './JSONUtils';
import URL from 'url-parse';
import { HC_SETTINGS_BY_ORG } from '../constants/config';
import { WEBHOOK_SOURCE_URL } from '../constants/api';
import { ZENDESK_REST_TEMPLATE } from '../constants/templateTypes';
import {
  CONNECTION_CATEGORY_DATABASE,
  CONNECTION_CATEGORY_FILE,
  CONNECTION_CATEGORY_TEMPLATIZED_API,
  CONNECTION_CATEGORY_TEMPLATIZED_EMAIL_SCRIPT,
  CONNECTION_CATEGORY_WEBHOOK,
  CONNECTOR_CODE_SQL_RUNNER,
} from '../constants/connectionCategories';
import { convertUTCToLocal } from './DateUtils';
import { compact, forEach, has, isEmpty, isUndefined, map, padStart } from 'lodash';
import { slugify } from 'new/utils/slugify';
import { userDataCategories } from 'js/constants/connectionCategories';

var _ = require('lodash');

function toPluralFormat(label, count, includeCount = true) {
  return pluralize(label, count, includeCount);
}

function renderAndJoin(
  values,
  renderer,
  delimiter = ', ',
  finalDelimiter = ' and ',
  limit = 0, // show at most these many items followed by "and <rest> more"
) {
  const limitedValues = limit > 0 ? _.slice(values, 0, limit) : values;
  const truncated = limitedValues.length < values.length;

  if (truncated) {
    limitedValues.push(`${values.length - limit} more`);
  }

  const list = [];

  _.forEach(limitedValues, (value, index) => {
    // instead of the last value, render "and <rest> more"
    list.push(truncated && index === limitedValues.length - 1 ? value : renderer(value, index));

    if (limitedValues.length > 1 && index < limitedValues.length - 1) {
      list.push(index === limitedValues.length - 2 ? finalDelimiter : delimiter);
    }
  });

  return list;
}

function getDatasetSharersCount(data = {}, summaryData = {}) {
  let sharersCount = 0;

  if (!_.isNil(summaryData) && _.has(summaryData, 'sharers')) {
    if (_.has(summaryData, 'sharers.total')) {
      sharersCount += summaryData.sharers.total;
    }
    if (_.has(summaryData, 'sharers.external')) {
      sharersCount += summaryData.sharers.external;
    }
  } else {
    if (_.has(data, 'sharers') && !_.isEmpty(data.sharers)) {
      sharersCount += data.sharers.length;
    }

    if (_.has(data, 'external_sharers') && !_.isEmpty(data.external_sharers)) {
      sharersCount += data.external_sharers.length;
    }
  }

  return sharersCount;
}

function getMetadataSamplesByAttrId(dataSamplesArray = []) {
  return getSamplesByAttrId(dataSamplesArray, true);
}

function getSamplesByAttrId(dataSamplesArray = [], getMetadata = false) {
  if (!_.isArray(dataSamplesArray) || 0 === dataSamplesArray.length) {
    //we don't have samples to match this schema
    return {};
  }

  //let's discard null or empty sample objects;
  let trimmedDataSamples = dataSamplesArray;
  // let trimmedDataSamples = _.filter(dataSamplesArray, sampleObj => {
  //   return !_.isEmpty(sampleObj.attrs);
  // });

  if (!_.isArray(trimmedDataSamples) || 0 === trimmedDataSamples.length) {
    return {};
  }
  //get all attribute keys first
  let allAttrs = _.reduce(
    trimmedDataSamples,
    (result, sampleObj) => {
      let attrsInSample = getMetadata === true ? _.keys(sampleObj.meta) : _.keys(sampleObj.attrs);
      // console.log(attrsInSample);
      return _.union(result, attrsInSample);
    },
    [],
  );
  // console.log('trimming data samples');
  // console.log(dataSamplesArray);
  // console.log(allAttrs);
  // console.log(trimmedDataSamples);

  let samplesByAttrId = _.reduce(
    trimmedDataSamples,
    (result, sampleObj) => {
      _.forEach(allAttrs, (attrName, idx) => {
        if (!_.has(result, attrName)) {
          result[attrName] = [];
        }
        let newValue = null;

        if (getMetadata === true) {
          newValue = _.has(sampleObj.meta, attrName) ? sampleObj.meta[attrName] : null;
        } else if (_.has(sampleObj.attrs, attrName)) {
          newValue = sampleObj.attrs[attrName];
        }

        result[attrName] = [...result[attrName], newValue];
      });
      return result;
    },
    {},
  );

  return samplesByAttrId;
}

function filterSchemaAttributes(flatSchema, filterText) {
  if (!filterText || 0 === filterText.length) {
    return flatSchema;
  }
  var filterByString = filterText.toLowerCase();
  var filteredSchema = _.filter(flatSchema, function (item) {
    return item.__name.toLowerCase().indexOf(filterByString) !== -1;
  });

  return filteredSchema;
}

function getAttrDisplayName(attr) {
  return _.replace(attr.__name, '.[*]', '');
}

function isFieldInSourceLocation(fieldName, connectionCategory) {
  let isFieldInSourceLocation = false;

  isFieldInSourceLocation =
    connectionCategory === CONNECTION_CATEGORY_FILE &&
    (fieldName === 'bucket' ||
      fieldName === 'prefix' ||
      fieldName === 'file' ||
      fieldName === 'path' ||
      fieldName === 'source_sub_type' ||
      fieldName === 'email_address');

  return isFieldInSourceLocation;
}

function isFieldInSinkLocation(fieldName, connectionCategory) {
  let isFieldInSinkLocation = false;

  switch (connectionCategory) {
    case CONNECTION_CATEGORY_FILE:
      isFieldInSinkLocation =
        fieldName === 'bucket' ||
        fieldName === 'prefix' ||
        fieldName === 'output.dir.name.pattern' ||
        fieldName === 'file' ||
        fieldName === 'path' ||
        fieldName === 'source_sub_type' ||
        fieldName === 'email_address';
      break;

    default:
  }

  return isFieldInSinkLocation;
}

function getLocationFromSourceConfig(
  sourceConfig,
  connectionCategory,
  shouldStripPrefix = false,
  prefixCustomPart = null,
  sourceId = null,
  currentOrg = null,
) {
  if (!sourceConfig) {
    return '';
  }

  if (connectionCategory === CONNECTION_CATEGORY_WEBHOOK) {
    let orgWebhookURL =
      currentOrg && _.has(currentOrg, 'org_webhook_host') ? currentOrg.org_webhook_host : null;
    return `${WEBHOOK_SOURCE_URL(orgWebhookURL)}/${sourceId}`;
  }

  if (_.has(sourceConfig, 'url.template')) {
    return sourceConfig['url.template'];
  }

  //TODO: Temp hack for using email type sources. Remove when Email source setup is designed.

  if (_.has(sourceConfig, 'source_sub_type') && sourceConfig.source_sub_type === 'email') {
    if (_.has(sourceConfig, 'email_address')) {
      return sourceConfig['email_address'];
    } else {
      return '';
    }
  }

  var pathString = '';

  if (!_.isEmpty(sourceConfig)) {
    if (connectionCategory === CONNECTION_CATEGORY_FILE || !connectionCategory) {
      if (_.isString(sourceConfig['ui.display_path'])) {
        pathString = sourceConfig['ui.display_path'];
      } else if (_.isString(sourceConfig.displayPath)) {
        pathString = sourceConfig.displayPath;
      } else if (_.isString(sourceConfig.path)) {
        pathString = sourceConfig.path;
      } else {
        /*
        sourceConfig has structure:
        {
          bucket: String (optional),
          prefix: String (optional),
          file: String (optional),
          path: String (optional)
        }
        Convert to bucket/prefix/file
        */
        if (_.isString(sourceConfig.bucket)) {
          pathString += `${sourceConfig.bucket}`;
        } else if (_.isString(sourceConfig.bucketName)) {
          pathString += `${sourceConfig.bucketName}`;
        }

        if (pathString.slice(-1) !== '/') {
          pathString += '/';
        }

        if (_.isString(sourceConfig.prefix)) {
          pathString += `${sourceConfig.prefix}`;
        }

        if (pathString.slice(-1) !== '/') {
          pathString += '/';
        }

        if (
          _.has(sourceConfig, 'output.dir.name.pattern') &&
          _.isString(sourceConfig['output.dir.name.pattern'])
        ) {
          let suffix = sourceConfig['output.dir.name.pattern'];
          pathString += `${suffix}`;
        }

        if (_.isString(sourceConfig.file)) {
          pathString += `/${sourceConfig.file}`;
        }
      }
    } else if (connectionCategory === CONNECTION_CATEGORY_DATABASE) {
      /*
      sourceConfig has structure:
      {
        database: String (optional),
        table: String (optional)
      }
      Convert to database/table
      */
      if (_.isString(sourceConfig.database)) {
        pathString = `${sourceConfig.database}`;
      }

      if (_.isString(sourceConfig.table)) {
        pathString += `/${sourceConfig.table}`;
      } else if (_.isString(sourceConfig.name)) {
        pathString += `/${sourceConfig.name}`;
      }
    }
  }

  return pathString;
}

function parsePartsFromURL(urlString = null) {
  let urlToParse = _.trimEnd(urlString, '/');
  let url = urlToParse ? new URL(urlString) : urlToParse;

  if (url) {
    let lastPath = _.last(_.split(url.pathname, '/'));
    return { hostname: url.hostname, pathname: url.pathname, lastPath };
  } else {
    return { hostname: urlString, pathname: '', lastPath: '' };
  }
}

function getSinkMappingFromAttributes(attributesArray, excludeByAttrName = {}) {
  const mapping = {
    mapping: {},
    fields_order: [],
  };

  _.forEach(attributesArray, (attr) => {
    if (!_.get(excludeByAttrName, attr.__name)) {
      mapping.mapping[attr.__name] = [attr.__name];
      mapping.fields_order.push(attr.__name);
    }
  });

  const mappingString = JSONUtils.stringifyJson(mapping);
  return { obj: mapping, str: mappingString };
}

function shouldHideField(field, values) {
  let shouldHide = false;
  //const { conditionalDisplay, displayOn, isHiddenField } = field;
  if (_.has(field, 'isHiddenField') && field.isHiddenField === true) {
    shouldHide = true;
  } else if (_.has(field, 'conditionalDisplay') && field.conditionalDisplay === true) {
    //TODO: Hack for now till we fix shouldRenderField logic
    if (_.has(field, 'inputType') && field.inputType !== 'button') {
      const displayOn = _.has(field, 'displayOn') ? field.displayOn : [];

      let shouldDisplay = true;
      _.each(displayOn, (critField, idx) => {
        if (
          (_.has(values, critField.name) || _.has(values, critField.fieldName)) &&
          values[critField.name] !== critField.value
        ) {
          shouldDisplay = false;
          return false;
        }
      });
      shouldHide = !shouldDisplay;
    }
  }

  return shouldHide;
}

function dependsOnField(field, name) {
  const { dependsOn } = field;
  if (!dependsOn) return false;
  if (dependsOn.anyOf || dependsOn.allOf) {
    const dependencies = dependsOn.anyOf || dependsOn.allOf;
    return dependencies.some((d) => d.name === name);
  }
  return dependsOn.name === name;
}

function getSectionElementId(section) {
  return 'section-heading-' + slugify(section);
}

function getSectionsFromFields(fields) {
  const fieldsBySection = {};
  const sections = [];

  forEach(fields, (field) => {
    const { section } = field;

    if (!has(fieldsBySection, section)) {
      fieldsBySection[section] = [];

      if (isUndefined(section)) {
        sections.unshift(section);
      } else {
        sections.push(section);
      }
    }

    fieldsBySection[section].push(field);
  });
  return [sections.filter((s) => !isEmpty(fieldsBySection[s])), fieldsBySection];
}

function getActiveSections(fields, data) {
  const [sections, fieldsBySection] = getSectionsFromFields(fields);

  return sections.filter((s) => {
    if (!s) return false;
    const sectionFields = compact(
      map(fieldsBySection[s], (field) => {
        if (
          UIUtils.shouldHideField(field, data) === true ||
          !UIUtils.shouldRenderField(field, data)
        ) {
          return false;
        }
        return true;
      }),
    );
    return !isEmpty(sectionFields);
  });
}

function shouldRenderField(field, values) {
  const { dependsOn, conditionalDisplay, inputType, displayOn } = field;
  let shouldRender = true;
  if (!_.isNil(dependsOn)) {
    if (_.has(dependsOn, 'allOf') || _.has(dependsOn, 'anyOf')) {
      let requiresAll = _.has(dependsOn, 'allOf') ? true : false;
      //iterate over each value of dependsOn, and should render if only all checks pass for allOf, and render if any check passes for anyOf

      let conditions = requiresAll === true ? dependsOn.allOf : dependsOn.anyOf;
      let shouldRenderChecks = [];
      _.forEach(conditions, (thisCondition, idx) => {
        let thisShouldRender = true;
        if (_.has(values, thisCondition.name)) {
          //could depend on one of many values of dependsOn.
          let valueOfDependsOn = values[thisCondition.name];
          if (_.has(thisCondition, 'notOneOfValues')) {
            thisShouldRender = !_.includes(thisCondition.notOneOfValues, valueOfDependsOn);
          } else if (_.has(thisCondition, 'oneOfValues')) {
            thisShouldRender = _.includes(thisCondition.oneOfValues, valueOfDependsOn);
          } else {
            thisShouldRender = valueOfDependsOn === thisCondition.value;
          }
        } else {
          thisShouldRender = !!thisCondition.ignoreIfEmpty;
        }
        shouldRenderChecks.push(thisShouldRender);
      });

      _.forEach(shouldRenderChecks, (thisCheck) => {
        if (requiresAll === true) {
          if (thisCheck === false) {
            shouldRender = false;
            return false;
          } else {
            shouldRender = true;
          }
        } else {
          if (thisCheck === true) {
            shouldRender = true;
            return false;
          } else {
            shouldRender = false;
          }
        }
      });
    } else {
      if (_.has(values, dependsOn.name)) {
        //could depend on one of many values of dependsOn.
        let valueOfDependsOn = values[dependsOn.name];
        if (_.has(dependsOn, 'notOneOfValues')) {
          shouldRender = !_.includes(dependsOn.notOneOfValues, valueOfDependsOn);
        } else if (_.has(dependsOn, 'oneOfValues')) {
          shouldRender = _.includes(dependsOn.oneOfValues, valueOfDependsOn);
        } else {
          shouldRender = valueOfDependsOn === dependsOn.value;
        }
      } else {
        shouldRender = dependsOn.ignoreIfEmpty;
      }
    }
  }

  // let shouldRender =
  //   !dependsOn ||
  //   (_.has(values, dependsOn.name) &&
  //     values[dependsOn.name] === dependsOn.value) ||
  //   (!_.has(values, dependsOn.name) && dependsOn.ignoreIfEmpty);

  //TODO: limiting conditionalDisplay to buttons only for now because there is some convoluted logic on stripping output even though all we just want to is to display/hide fields
  if (inputType !== 'button' || shouldRender === false) {
    return shouldRender;
  }

  if (inputType === 'button' && conditionalDisplay === true && shouldRender === true) {
    _.each(displayOn, (critField, idx) => {
      if (_.has(values, critField.name) && values[critField.name] !== critField.value) {
        shouldRender = false;
        return false;
      }
    });
  }

  return shouldRender;
}

function configWithMissingDefaults(currentConfig, fieldMapping, merge = true) {
  // for each field, if it should render and is not present in current config and has defaults, add to defaults
  const initialValueReducer = merge ? { ...currentConfig } : {};

  let updatedConfig = _.reduce(
    fieldMapping,
    (accConfig, currentField) => {
      if (_.has(currentConfig, currentField.name)) {
        let thisFieldConfig = currentConfig[currentField.name];
        //TODO: check validations properly
        if (
          !_.has(currentField, 'validations.notEmpty') &&
          !_.has(currentField, 'validations.notNull') &&
          !_.has(currentField, 'validations.mutualExclusion') &&
          !_.has(currentField, 'validations.pattern')
        ) {
          accConfig[currentField.name] = thisFieldConfig;
        } else if (_.has(currentField, 'validations.notNull')) {
          let shouldNotBeNull = currentField.validations.notNull;

          if (!shouldNotBeNull || !_.isNil(thisFieldConfig)) {
            accConfig[currentField.name] = thisFieldConfig;
          }
        } else if (_.has(currentField, 'validations.notEmpty')) {
          let shouldNotBeEmpty = currentField.validations.notEmpty;

          if (!shouldNotBeEmpty || currentField.type !== 'string' || thisFieldConfig !== '') {
            accConfig[currentField.name] = thisFieldConfig;
          }
        } else if (_.has(currentField, 'validations.mutualExclusion')) {
          currentField.validations.mutualExclusion.forEach((exclusion, idx) => {
            if (accConfig[currentField.name].length === 1 && accConfig[exclusion]) {
              accConfig[exclusion] = '';
            }
          });
        } else if (_.has(currentField, 'validations.pattern')) {
          // Pattern validation is handled in validateFieldValidations, not in configWithMissingDefaults
          // This is just for data transformation, so we include the value
          accConfig[currentField.name] = thisFieldConfig;
        }
      } else {
        let shouldRender = shouldRenderField(currentField, currentConfig);
        if (shouldRender === true) {
          if (_.has(currentField, 'default')) {
            accConfig[currentField.name] = currentField?.default;
          } else if (currentField.type === 'nonce') {
            accConfig[currentField.name] = generateNonce();
          }
        }
      }

      return accConfig;
    },
    initialValueReducer,
  );

  //now that all missing fields are populated, we need to populate missing fields that require conditional defaults

  updatedConfig = _.reduce(
    fieldMapping,
    (accConfig, currentField, idx) => {
      if (
        !_.has(updatedConfig, currentField.name) ||
        (_.has(currentField, 'isHiddenField') && currentField.isHiddenField === true)
      ) {
        let shouldRender = shouldRenderField(currentField, currentConfig);
        if (
          shouldRender === true &&
          _.has(currentField, 'conditionalDefault') &&
          currentField.conditionalDefault === true
        ) {
          let newConfig = fillConditionalDefault(updatedConfig, fieldMapping);
          if (_.has(newConfig, currentField.name)) {
            accConfig[currentField.name] = newConfig[currentField.name];
          }
        }
      }

      return accConfig;
    },
    updatedConfig,
  );

  // fill param_ref's
  updatedConfig = _.reduce(
    fieldMapping,
    (accConfig, currentField, idx) => {
      if (_.has(currentField, 'param_ref')) {
        accConfig[currentField.name] = updatedConfig[currentField.param_ref];
      }

      return accConfig;
    },
    updatedConfig,
  );

  //TODO: this should ideally be conditional default. Need to handle notOneOfValues in conditional defaults.
  //Data Format default for GDrive should be Spreadsheet depending on mime type
  if (
    _.has(updatedConfig, 'ui.selected_file_mime_type') &&
    updatedConfig['ui.selected_file_mime_type'] === 'application/vnd.google-apps.spreadsheet' &&
    _.has(updatedConfig, 'data_format') &&
    updatedConfig['data_format'] !== 'GOOGLE_SPREADSHEET'
  ) {
    updatedConfig['data_format'] = 'GOOGLE_SPREADSHEET';
  }

  return updatedConfig;
}

function reduceConfig(values, fields, keepNonFieldValues = true) {
  // only include the values whose fields should be rendered
  const fieldsByName = _.groupBy(fields, 'name');

  return _.pickBy(values, (value, key) => {
    const fieldsForKey = fieldsByName[key];
    const field = fieldsForKey && fieldsForKey[0];

    return field
      ? shouldRenderField(field, values) && !(field.excludeIfEmpty && _.isEmpty(value))
      : keepNonFieldValues;
  });
}

function unflattenTree(flatTree) {
  const flatTreeClone = _.cloneDeep(flatTree);
  // replace child paths in children array by child objects
  for (let nodePath in flatTreeClone) {
    const node = flatTreeClone[nodePath];
    node.children = _.has(node, 'children')
      ? sortTreeNodes(node.children.map((childPath) => flatTreeClone[childPath]))
      : [];
  }

  // pick and return array of top-level nodes
  return sortTreeNodes(_.values(_.pickBy(flatTreeClone, (node) => !node.parent)));
}

function sortTreeNodes(nodes) {
  return _.sortBy(nodes, [
    (node) => (node.type === 'folder' ? 0 : 1),
    (node) => _.toLower(node.displayName),
  ]);
}

function fillConditionalDefault(config, fields, field = null, value = null) {
  let _config = { ...config };

  let conditionalDefaultParents = [];
  let fieldsToClean = [];
  _.forEach(fields, (f) => {
    // get conditional values hierarchy
    if (f.conditionalDefault) {
      _.forEach(f.defaults, (d) => {
        _.forEach(d.dependsOnValues, (v) => {
          conditionalDefaultParents.push({
            field: d.dependsOn,
            value: v,
            destinationField: f.name,
            destinationValue: d.defaultValue,
          });
        });
      });
    }
    //get fields to be cleaned because dependsOn parent field has changed value
    if (f.dependsOn) {
      if (_.has(f.dependsOn, 'allOf') || _.has(f.dependsOn, 'anyOf')) {
        let requiresAll = _.has(f.dependsOn, 'allOf') ? true : false;
        //iterate over each value of dependsOn, and should render if only all checks pass for allOf, and render if any check passes for anyOf
        let shouldClean = false;
        let conditions = requiresAll === true ? f.dependsOn.allOf : f.dependsOn.anyOf;
        let shouldCleanChecks = [];
        _.forEach(conditions, (thisCondition, idx) => {
          if (_.has(_config, thisCondition.name)) {
            //could depend on one of many values of dependsOn.
            let thisShouldClean = false;

            let valueOfDependsOn = _config[thisCondition.name];

            if (_.has(thisCondition, 'notOneOfValues')) {
              thisShouldClean = _.includes(thisCondition.notOneOfValues, valueOfDependsOn);
            } else if (_.has(thisCondition, 'oneOfValues')) {
              thisShouldClean = !_.includes(thisCondition.oneOfValues, valueOfDependsOn);
            } else {
              thisShouldClean = valueOfDependsOn !== thisCondition.value;
            }
            shouldCleanChecks.push(thisShouldClean);
          }
        });

        _.forEach(shouldCleanChecks, (thisCheck) => {
          if (requiresAll === false) {
            //if any dependsOn is good enough for rendering, all clean checks must be true for it to be cleaned up.

            if (thisCheck === false) {
              shouldClean = false;
              return false;
            } else {
              shouldClean = true;
            }
          } else {
            //if all dependsOn must be present for rendering, if any clean check is passed it must be cleaned up.
            if (thisCheck === true) {
              shouldClean = true;
              return false;
            } else {
              shouldClean = false;
            }
          }
        });

        if (shouldClean === true) {
          fieldsToClean.push(f.name);
        }
      } else {
        if (_.has(_config, f.dependsOn.name)) {
          //could depend on one of many values of dependsOn.
          let shouldClean = false;

          let valueOfDependsOn = _config[f.dependsOn.name];

          if (_.has(f.dependsOn, 'notOneOfValues')) {
            shouldClean = _.includes(f.dependsOn.notOneOfValues, valueOfDependsOn);
          } else if (_.has(f.dependsOn, 'oneOfValues')) {
            shouldClean = !_.includes(f.dependsOn.oneOfValues, valueOfDependsOn);
          } else {
            shouldClean = valueOfDependsOn !== f.dependsOn.value;
          }
          if (shouldClean === true) {
            fieldsToClean.push(f.name);
          }
        }
      }
    }
  });

  _.forEach(fields, (f) => {
    if (_.isNil(_config[f.name])) {
      if (_.has(f, 'default')) {
        _config[f.name] = f.default;
      } else if (f.type === 'nonce') {
        _config[f.name] = generateNonce();
      }
    }
    //clean fields that are not longer visible
    if (_.includes(fieldsToClean, f.name)) {
      delete _config[f.name];
    }
  });

  if (field && value) {
    let fieldObjs = _.filter(
      conditionalDefaultParents,
      (p) => p.field === field && p.value === value,
    );
    _.forEach(fieldObjs, (fieldObj) => {
      _config[fieldObj.destinationField] = fieldObj.destinationValue;
    });
  } else {
    _.forEach(conditionalDefaultParents, (p) => {
      if (p.value === _config[p.field]) {
        _config[p.destinationField] = p.destinationValue;
      }
    });
  }

  //TODO: NEX-2881 : Temp hack for zendesk.access_token in request.headers
  if (field === 'zendesk.access_token' && _config['rest.template_type'] === ZENDESK_REST_TEMPLATE) {
    let trimmedValue = value ? _.trim(value) : '';
    let newRequestHeader = '';
    let currentRequestHeader = _.has(_config, 'request.headers')
      ? _.trim(_config['request.headers'])
      : '';

    if (currentRequestHeader !== '') {
      let requestHeaderArr = _.split(currentRequestHeader, ',');

      let newRequestHeaderArr = _.filter(requestHeaderArr, (h) => {
        return !h.startsWith('Authorization: Bearer');
      });
      if (trimmedValue !== '') {
        newRequestHeaderArr.push(`Authorization: Bearer ${trimmedValue}`);
      }
      newRequestHeader = _.join(newRequestHeaderArr, ',');
    } else {
      newRequestHeader = trimmedValue !== '' ? `Authorization: Bearer ${trimmedValue}` : '';
    }

    _config['request.headers'] = newRequestHeader;
    if ((_.has(_config), 'zendesk.access_token')) {
      _config['zendesk.access_token'] = trimmedValue;
    }
    // if
  }

  return _config;
}

function castToFieldType(values, fields) {
  const fieldsByName = _.groupBy(fields, 'name');

  return _.mapValues(values, (value, fieldName) => {
    const field = fieldsByName[fieldName] && fieldsByName[fieldName][0];
    const type = field && field.type;

    switch (type) {
      case 'number':
        return _.toNumber(value);

      case 'int':
        return _.toInteger(value);

      case 'boolean':
        if (value === 'false') {
          return false;
        }

        return !!value;

      default:
        return value;
    }
  });
}

function tagsToOps(tags) {
  return _.map(tags || {}, (t) => {
    return { label: t, value: t };
  });
}

function opsToTags(ops) {
  let tags = _.map(ops, (op) => op.value);
  return tags;
}

/**
 * Builds a UTC cron expression from the options provided in local time zone.
 * This function is inverse of parseCron().
 */
function getCron(options, toUTC = true) {
  const { interval, hh, mm, aa, dayOfWeek, dayOfMonth, timestamp, n } = options;
  const numIntervals = n || 2;

  let date;

  if (interval === 'Once') {
    date = new Date(timestamp);
  } else {
    let minutes = 0;
    let hours = 0;

    if (mm) {
      minutes = parseInt(mm, 10) || 0;
    }

    if (hh) {
      hours = parseInt(hh, 10) || 12;

      if (hours !== 12 && aa === 'PM') {
        hours += 12;
      } else if (hours === 12 && aa === 'AM') {
        hours = 0;
      }
    }

    date = new Date();
    date.setMinutes(minutes);
    date.setHours(hours);

    if (interval === 'Week') {
      const dayDiff = date.getDay() - dayOfWeek;
      date.setDate(date.getDate() - dayDiff);
    } else if (dayOfMonth) {
      date.setDate(dayOfMonth);
    }
  }

  const cronMinutes = toUTC ? date.getUTCMinutes() : date.getMinutes();
  const cronHours = toUTC ? date.getUTCHours() : date.getHours();
  const cronDayOfWeek = (toUTC ? date.getUTCDay() : date.getDay()) + 1;
  const cronDayOfMonth = toUTC ? date.getUTCDate() : date.getDate();
  const cronMonth = (toUTC ? date.getUTCMonth() : date.getMonth()) + 1;
  const cronYear = toUTC ? date.getUTCFullYear() : date.getFullYear();
  let allCronMinutes;

  switch (interval) {
    case 'Once':
      return `0 ${cronMinutes} ${cronHours} ${cronDayOfMonth} ${cronMonth} ? ${cronYear}`;

    case '15 Minutes':
      allCronMinutes = [
        cronMinutes,
        (cronMinutes + 15) % 60,
        (cronMinutes + 30) % 60,
        (cronMinutes + 45) % 60,
      ].join(',');

      return `0 ${allCronMinutes} * * * ? *`;

    case '30 Minutes':
      allCronMinutes = [cronMinutes, (cronMinutes + 30) % 60].join(',');

      return `0 ${allCronMinutes} * * * ? *`;

    case 'Hour':
      return `0 ${cronMinutes} 0/1 * * ? *`;

    case 'N Hours':
      return `0 ${cronMinutes} 0/${numIntervals} * * ? *`;

    case 'Week':
      return `0 ${cronMinutes} ${cronHours} ? * ${cronDayOfWeek} *`;

    case 'Month':
      return `0 ${cronMinutes} ${cronHours} ${cronDayOfMonth} 1/1 ? *`;

    case 'N Months':
      return `0 ${cronMinutes} ${cronHours} ${cronDayOfMonth} 1/${numIntervals} ? *`;

    case 'N Days':
      return `0 ${cronMinutes} ${cronHours} 1/${numIntervals} * ? *`;

    // Day
    default:
      return `0 ${cronMinutes} ${cronHours} 1/1 * ? *`;
  }
}

/**
 * Returns parts of the UTC cron in local time zone. This function is inverse of
 * getCron().
 */
function parseCron(cron) {
  const matches = cron.match(/^0 (\d+) (\d+) (\d+) (\d+) \? (\d+)$/);

  if (matches) {
    const timestamp = convertUTCToLocal(
      new Date(matches[5], matches[4] - 1, matches[3], matches[2], matches[1]).getTime(),
    );

    return {
      interval: 'Once',
      timestamp,
    };
  }

  const cronParts = cron.split(' ');
  const cronDate = new Date();
  const cronPartMatches = _.map(cronParts, (part) => part.match(/^\d+/));

  if (cronPartMatches[1]) {
    cronDate.setMinutes(cronPartMatches[1]);
  }

  if (cronPartMatches[2]) {
    cronDate.setHours(cronPartMatches[2]);
  }

  if (cronPartMatches[3] && !cronParts[3].startsWith('1/')) {
    cronDate.setDate(cronPartMatches[3]);
  }

  if (cronPartMatches[5]) {
    const dayDiff = cronDate.getDay() - (cronPartMatches[5] - 1);
    cronDate.setDate(cronDate.getDate() - dayDiff);
  }

  const cronLocalDate = new Date(convertUTCToLocal(cronDate.getTime()));
  const result = {};

  if (cronParts[1] === '0/1' && cronParts[2] === '*') {
    result.interval = 'Continuously';
  } else if (/^\d+,\d+,\d+,\d+$/.test(cronParts[1]) && cronParts[2] === '*') {
    result.interval = '15 Minutes';
  } else if (/^\d+,\d+$/.test(cronParts[1]) && cronParts[2] === '*') {
    result.interval = '30 Minutes';
  } else if (_.startsWith(cronParts[2], '0/')) {
    const n = cronParts[2].slice(cronParts[2].indexOf('/') + 1);
    const numIntervals = parseInt(n, 10);

    if (numIntervals > 1) {
      result.interval = 'N Hours';
      result.n = n;
    } else {
      result.interval = 'Hour';
    }
  } else if (cronParts[3] === '?' && cronParts[4] === '*' && cronPartMatches[5]) {
    result.interval = 'Week';
    result.dayOfWeek = String(cronLocalDate.getDay());
  } else if (_.startsWith(cronParts[4], '1/')) {
    const n = cronParts[4].slice(cronParts[4].indexOf('/') + 1);
    const numIntervals = parseInt(n, 10);

    if (numIntervals > 1) {
      result.interval = 'N Months';
      result.n = n;
    } else {
      result.interval = 'Month';
    }

    if (cronPartMatches[3]) {
      result.dayOfMonth = String(cronLocalDate.getDate());
    }
  } else if (/^\d+$/.test(cronParts[1]) && cronParts[3].startsWith('1/')) {
    const n = cronParts[3].slice(cronParts[3].indexOf('/') + 1);
    const numIntervals = parseInt(n, 10);

    if (numIntervals > 1) {
      result.interval = 'N Days';
      result.n = n;
    } else {
      result.interval = 'Day';
    }
  }

  result.mm = _.padStart(cronLocalDate.getMinutes(), 2, '0');

  if (cronPartMatches[2]) {
    const hour = cronLocalDate.getHours() || 0;
    result.hh = _.padStart(hour % 12 || 12, 2, '0');
    result.aa = hour < 12 ? 'AM' : 'PM';
  }

  // If getCron can't reproduce the original cron, our classification is lossy —
  // return {} so callers show a read-only raw-cron display instead of silently
  // rewriting it on save. Week is excluded because its dayOfWeek offset makes the
  // round-trip unreliable. Month/N Months are intentionally not excluded: dayOfMonth
  // is stored in local time, so a cron near UTC midnight may parse to the wrong day;
  // read-only is safer than saving a corrupted value.
  if (result.interval && result.interval !== 'Week' && getCron(result) !== cron) {
    return {};
  }

  return result;
}

function stringToParagraphs(string) {
  return _.map(string.split(/\n+/), (text, idx) => <p key={idx}>{text}</p>);
}

const getWebhookSampleRequest = (
  url,
  sanitizedUrl,
  sourceConfig = {},
  contentType = 'application/json',
  data = `{
    "id": 1,
    "name":"one"
  }`,
) => {
  const hashEnabled = _.has(sourceConfig, 'hash.enabled') ? sourceConfig['hash.enabled'] : false;

  let payload = `-d '${data}'`;
  if (hashEnabled === true) {
    const hashParam = _.has(sourceConfig, 'hash_request_param')
      ? sourceConfig.hash_request_param
      : `<REQUEST PARAM CONTAINING HASH>`;
    payload = `-H ${hashParam}:<HASH_VALUE> \
     -d '${data}'
    `;
  }

  return {
    sample: `curl -X POST ${url} \
 -H 'Content-Type: ${contentType}' \
 ${payload}`,
    sanitizedSample: `curl -X POST ${sanitizedUrl} \
 -H 'Content-Type: ${contentType}' \
 ${payload}`,
  };
};

const disallowSourceCopy = (connectionCategory = null, sourceData = null) => {
  // Check if connectionCategory is part of userDataCategories
  if (connectionCategory && userDataCategories.includes(connectionCategory)) {
    return true;
  }

  // Additional fallback: Check source types directly for user data sources
  if (sourceData?.source_type) {
    const userDataSourceTypes = ['nexla_rest', 'file_upload'];
    if (userDataSourceTypes.includes(sourceData.source_type)) {
      return true;
    }
  }

  return false;
};

const isSourceAutoGenerated = (sourceData = null) => {
  if (!sourceData) {
    return false;
  }
  let isAutoGenerated = false;

  if (_.has(sourceData, 'data_sinks.id')) {
    isAutoGenerated = true;
  } else if (_.has(sourceData, 'auto_generated')) {
    isAutoGenerated = sourceData.auto_generated;
  }
  return isAutoGenerated;
};

function intersperse(arr, sep) {
  if (arr.length === 0) {
    return [];
  }

  return arr.slice(1).reduce(
    (xs, x, idx) => {
      const separator = typeof sep === 'function' ? sep(idx) : sep;
      return xs.concat([separator, x]);
    },
    [arr[0]],
  );
}

const formatsWithColumns = ['csv', 'tsv', 'xls', 'xlsx', 'GOOGLE_SPREADSHEET'];
const headerlessFormats = ['edi', 'avro', 'parquet', 'orc'];

function buildMapping(
  connectionCategory,
  data_format,
  mapping,
  initialMapping = null,
  overrideMapping = { mode: 'manual' },
) {
  switch (connectionCategory) {
    case CONNECTION_CATEGORY_FILE:
      if (formatsWithColumns.indexOf(data_format) > -1) {
        return {
          ...mapping,
          mode: 'manual',
        };
      } else {
        return {
          mode: 'auto',
        };
      }
    case CONNECTION_CATEGORY_DATABASE:
      return {
        ...initialMapping,
        ...overrideMapping,
        mapping,
      };
    case CONNECTION_CATEGORY_TEMPLATIZED_API: {
      return {
        ...initialMapping,
        mode: 'auto',
        mapping,
      };
    }
    default:
      return {
        mode: 'auto',
      };
  }
}

/**
 * Returns the mapping value to pass to buildMapping for the given connection category and
 * sinkMapping. For database connectors, getSinkMappingFromAttributes returns
 * { mapping: {...}, fields_order }; passing that whole object would produce
 * sink_config.mapping.mapping.mapping. This returns only the inner .mapping when present
 * so the result is sink_config.mapping.mapping, matching the UI-configured structure.
 *
 * Expected sink_config shape (database destination):
 *   "sink_config": {
 *     "table": "01_test_nex_16614",
 *     "database": "qa",
 *     "mapping": {
 *       "mapping": { "debut": ["debut"], "name": ["name"], ... },
 *       "fields_order": ["debut", "debut.anime", "name", ...],
 *       "mode": "manual"
 *     },
 *     "insert.mode": "INSERT",
 *     "sink_type": "mysql"
 *   }
 */
function getMappingForBuild(connectionCategory, sinkMapping) {
  if (connectionCategory === CONNECTION_CATEGORY_DATABASE && sinkMapping?.obj?.mapping != null) {
    return sinkMapping.obj.mapping;
  }
  return sinkMapping?.obj;
}

function buildTemplatizedAPIConfigFromSource(source) {
  if (!source.template_config) {
    const endpointId =
      source.vendor_endpoint !== undefined && source.vendor_endpoint !== null
        ? source.vendor_endpoint
        : undefined;
    const out = {
      'start.cron': source?.source_config?.['start.cron'],
    };
    if (endpointId !== undefined) {
      out.vendor_endpoint_id = endpointId;
    }
    return out;
  }

  let {
    flow_triggers,
    vendor_endpoint,
    template_config: { 'rest.iterations': iterations, ...templatizedAPIConfig },
  } = source;

  templatizedAPIConfig.vendor_endpoint_id = vendor_endpoint;

  _.forEach(iterations, (iteration) => {
    if (!_.isEmpty(iteration)) {
      _.forEach(iteration, (value, key) => {
        templatizedAPIConfig[`rest.iterations.${key}`] = value;
      });
    }
  });

  if (flow_triggers?.length > 0) {
    templatizedAPIConfig.flow_triggers = flow_triggers;
  }

  return templatizedAPIConfig;
}

function buildSourceFromTemplatizedAPIConfig(templatizedAPIConfig, configFields) {
  const clonedTemplatizedAPIConfig = structuredClone(templatizedAPIConfig);
  const { name, description } = clonedTemplatizedAPIConfig;
  delete clonedTemplatizedAPIConfig?.name;
  delete clonedTemplatizedAPIConfig?.description;

  const isCodeMode = clonedTemplatizedAPIConfig['ui.mode'] === 'code';
  const config = isCodeMode ? clonedTemplatizedAPIConfig : {};

  const source = {
    vendor_endpoint_id: templatizedAPIConfig.vendor_endpoint_id,
    [isCodeMode ? 'source_config' : 'template_config']: config,
  };

  if (name) {
    source.name = name;
  }

  if (description) {
    source.description = description;
  }

  _.forEach(configFields, (field) => {
    if (
      field.name !== 'vendor_endpoint_id' &&
      _.has(templatizedAPIConfig, field.name) &&
      shouldRenderField(field, templatizedAPIConfig)
    ) {
      const value = templatizedAPIConfig[field.name];

      if (field.name.startsWith('rest.iterations.')) {
        if (!config['rest.iterations']) {
          config['rest.iterations'] = [];
        }

        const iterationIndices = field.iterationIndices || [0];
        const effectiveName = field.name.substring(16);

        _.forEach(iterationIndices, (iterationIndex) => {
          if (!config['rest.iterations'][iterationIndex]) {
            config['rest.iterations'][iterationIndex] = {};
          }

          config['rest.iterations'][iterationIndex][effectiveName] = value;
        });
      } else {
        config[field.name] = value;
      }
    }
  });

  // fill empty rest.iterations
  _.forEach(config['rest.iterations'], (iteration, index) => {
    if (!iteration) {
      config['rest.iterations'][index] = {};
    }
  });

  if (templatizedAPIConfig.flow_triggers) {
    config.flow_triggers = templatizedAPIConfig.flow_triggers;
  }

  if (templatizedAPIConfig['adaptive_flow']) {
    config['adaptive_flow'] = templatizedAPIConfig['adaptive_flow'];
  }

  // ensure past start.cron is present in template_config to avoid backend validation errors
  // important for API Server targets where start.cron might not be provided
  if (!config['start.cron']) {
    // Create a timestamp that's exactly 10 minutes ago
    const tenMinutesAgo = Date.now() - 10 * 60 * 1000;
    const pastCron = getCron({
      interval: 'Once',
      timestamp: tenMinutesAgo,
    });
    config['start.cron'] = templatizedAPIConfig['start.cron'] || pastCron;
  }

  return source;
}

function buildTemplatizedScriptConfigFromSource(source, fields, credentialEntities) {
  if (!source.script_config) {
    return source;
  }

  const {
    vendor_endpoint,
    script_config: { parameters, credentials },
  } = source;
  const templatizedScriptConfig = { vendor_endpoint_id: vendor_endpoint };
  const scriptCredentialsData = {};

  _.forEach(credentials, (credential, index) => {
    const fieldName = credentials.length > 1 ? `credentials.id_${index}` : 'credentials.id';
    // const fieldName = `credentials.id_${index}`;
    templatizedScriptConfig[fieldName] = credential.id;
    scriptCredentialsData[fieldName] = {
      isNewCredential: false,
      selectedCredentialId: credential.id,
      selectedCredentialInfo: credentialEntities && credentialEntities[credential.id],
    };
  });

  _.forEach(parameters, (value, key) => {
    switch (key) {
      case 'vendor_endpoint':
      case 'vendor':
        // ignore these params
        break;

      case 'script.cron':
        templatizedScriptConfig[key] = value;
        break;

      default:
        templatizedScriptConfig[`parameters.${key}`] = value;
    }
  });

  _.forEach(fields, (field) => {
    if (field.path_in_resource) {
      templatizedScriptConfig[field.name] = _.get(source, field.path_in_resource);
    }
  });

  return { templatizedScriptConfig, scriptCredentialsData };
}

// sql_runner-specific version with enhanced handling
function buildTemplatizedScriptConfigFromSqlRunnerSource(source, fields, credentialEntities) {
  if (!source.script_config) {
    return source;
  }

  const {
    vendor_endpoint,
    script_config: { parameters, credentials },
  } = source;
  const templatizedScriptConfig = { vendor_endpoint_id: vendor_endpoint };
  const scriptCredentialsData = {};

  _.forEach(credentials, (credential, index) => {
    const fieldName = credentials.length > 1 ? `credentials.id_${index}` : 'credentials.id';
    templatizedScriptConfig[fieldName] = credential.id;
    scriptCredentialsData[fieldName] = {
      isNewCredential: false,
      selectedCredentialId: credential.id,
      selectedCredentialInfo: credentialEntities && credentialEntities[credential.id],
    };
  });

  // Enhanced parameter handling with guards and script.start.cron support
  if (parameters && _.isObject(parameters)) {
    _.forEach(parameters, (value, key) => {
      switch (key) {
        case 'vendor_endpoint':
        case 'vendor':
          break;
        case 'script.cron':
        case 'script.start.cron':
          templatizedScriptConfig[key] = value;
          if (key === 'script.start.cron') {
            templatizedScriptConfig['parameters.script.start.cron'] = value;
          }
          break;
        default:
          templatizedScriptConfig[`parameters.${key}`] = value;
      }
    });
  }

  // Guarded field handling
  if (fields && _.isArray(fields)) {
    _.forEach(fields, (field) => {
      // Skip credential fields - they're handled separately below
      if (field.type === 'script_credential') {
        return;
      }
      if (field.path_in_resource) {
        const val = _.get(source, field.path_in_resource);
        if (val !== undefined) {
          templatizedScriptConfig[field.name] = val;
        }
      }
    });
  }

  // =============================================================================
  // CREDENTIAL MATCHING LOGIC (LOADING FORM)
  // =============================================================================
  // SQL RUNNER SPECIFIC BEHAVIOR:
  //
  // For sql_runner, the API does NOT return script_config.credentials[] - it's always empty.
  // The credential is stored ONLY at source.data_credentials_id (root level).
  //
  // The form UI has multiple virtual credential fields (one per database type):
  // - credentials.id_0 for Snowflake (shown when storage_type="snowflake")
  // - credentials.id_1 for Redshift (shown when storage_type="redshift")
  // - etc.
  //
  // These field names (credentials.id_0, credentials.id_1) are artifacts of the connector
  // definition - they exist ONLY for UI rendering to determine which credential dropdown to show.
  // They should NOT be stored in script_config.parameters.
  //
  // Example field structure in connector config:
  // [
  //   { name: "credentials.id_0", type: "script_credential", credential_type: "snowflake",
  //     dependsOn: { name: "parameters.storage_type", oneOfValues: ["snowflake"] } },
  //   { name: "credentials.id_1", type: "script_credential", credential_type: "redshift",
  //     dependsOn: { name: "parameters.storage_type", oneOfValues: ["redshift", "postgres"] } }
  // ]
  //
  // LOADING LOGIC:
  // For sql_runner, the API does NOT populate script_config.credentials[] - it's always empty.
  // So the forEach loop at lines 1422-1432 (above) has nothing to iterate over and is skipped.
  // We always fall into this fallback section which reads source.data_credentials_id and
  // populates the correct UI field based on storage_type matching.
  //
  // Example: Source has storage_type="snowflake" and data_credentials_id=123
  // → We populate credentials.id_0=123 (the Snowflake field)
  // → Form shows credential 123 pre-selected in the Snowflake credential dropdown
  // =============================================================================

  // For sql_runner: API doesn't return script_config.credentials, so we read from data_credentials_id
  // and populate the matching credential field so the form shows the existing credential
  const hasNoCredentialsFromApi =
    !credentials || !_.isArray(credentials) || credentials.length === 0;
  if (
    hasNoCredentialsFromApi &&
    source.data_credentials_id != null &&
    _.size(scriptCredentialsData) === 0 &&
    fields &&
    _.isArray(fields)
  ) {
    const credentialId = source.data_credentials_id;
    const credentialInfo = credentialEntities && credentialEntities[credentialId];

    // Only populate the credential field that matches both:
    // 1. The credential type of the saved credential (e.g. "snowflake", "redshift")
    // 2. The current storage_type dependency (e.g. field should be shown when storage_type="snowflake")
    if (credentialInfo && credentialInfo.credentials_type) {
      const storageType = parameters && parameters['storage_type'];

      // Find and populate ONLY the credential field that matches storage_type
      // We stop after finding the first match to ensure we only populate one field
      let credentialPopulated = false;
      for (let i = 0; i < fields.length; i++) {
        const field = fields[i];
        if (
          !credentialPopulated &&
          field.type === 'script_credential' &&
          field.name &&
          field.credential_type === credentialInfo.credentials_type
        ) {
          // Now check if this field's dependsOn condition matches the current storage_type
          // This ensures we populate the right credential field for the selected database type
          let shouldPopulate = true;
          if (storageType && field.dependsOn) {
            const { dependsOn } = field;

            // Handle complex dependency structures: anyOf (OR logic) or allOf (AND logic)
            // Example: { anyOf: [{ name: "parameters.storage_type", oneOfValues: ["redshift", "postgres"] }] }
            if (dependsOn.anyOf || dependsOn.allOf) {
              const dependencies = dependsOn.anyOf || dependsOn.allOf;
              shouldPopulate = dependencies.some((dep) => {
                if (dep.name === 'parameters.storage_type') {
                  // Check if storage_type matches one of the allowed values
                  if (dep.oneOfValues) {
                    const matches = dep.oneOfValues.includes(storageType);
                    return matches;
                  } else if (dep.value !== undefined) {
                    // Single value match
                    const matches = dep.value === storageType;
                    return matches;
                  }
                }
                return false;
              });
            }
            // Handle simple dependency structure
            // Example: { name: "parameters.storage_type", oneOfValues: ["snowflake"] }
            else if (dependsOn.name === 'parameters.storage_type') {
              if (dependsOn.oneOfValues) {
                shouldPopulate = dependsOn.oneOfValues.includes(storageType);
              } else if (dependsOn.value !== undefined) {
                shouldPopulate = dependsOn.value === storageType;
              }
            }
          }

          // If all conditions are met, populate this credential field
          if (shouldPopulate) {
            scriptCredentialsData[field.name] = {
              isNewCredential: false,
              selectedCredentialId: credentialId,
              selectedCredentialInfo: credentialInfo,
            };
            // Also set in templatizedScriptConfig so it's available for saving
            templatizedScriptConfig[field.name] = credentialId;
            credentialPopulated = true;
            break; // Stop after finding the first matching field
          }
        }
      }
    }
  }

  return { templatizedScriptConfig, scriptCredentialsData };
}

function buildSourceFromTemplatizedScriptConfig(templatizedScriptConfig, configFields) {
  const source = buildSourceFromTemplatizedAPIConfig(templatizedScriptConfig, configFields);
  const configKey = _.has(source, 'source_config') ? 'source_config' : 'template_config';
  source.script_config = { config: source[configKey] };
  if (templatizedScriptConfig.flow_triggers) {
    source.script_config.flow_triggers = templatizedScriptConfig.flow_triggers;
  }
  delete source[configKey];

  const { config } = source.script_config;
  _.forEach(configFields, (field) => {
    if (field.path_in_resource && _.has(config, field.name)) {
      _.set(source, field.path_in_resource, config[field.name]);
    }
  });

  return source;
}

// sql_runner-specific version
function buildSqlRunnerSourceFromTemplatizedScriptConfig(templatizedScriptConfig, configFields) {
  const source = buildSourceFromTemplatizedAPIConfig(templatizedScriptConfig, configFields);
  const configKey = _.has(source, 'source_config') ? 'source_config' : 'template_config';
  source.script_config = { config: source[configKey] };
  if (templatizedScriptConfig.flow_triggers) {
    source.script_config.flow_triggers = templatizedScriptConfig.flow_triggers;
  }
  delete source[configKey];

  const { config } = source.script_config;
  _.forEach(configFields, (field) => {
    // Skip credential fields here - they're handled separately below
    if (field.type === 'script_credential') {
      return;
    }
    if (field.path_in_resource && _.has(config, field.name)) {
      _.set(source, field.path_in_resource, config[field.name]);
    }
  });

  // =============================================================================
  // CREDENTIAL MATCHING LOGIC (SAVING FORM)
  // =============================================================================
  // Goal: Extract the credential ID from the form and set it as source.data_credentials_id
  // (root level) to be sent to the API. The credential should NOT be stored in script_config.
  //
  // SQL RUNNER SPECIFIC BEHAVIOR:
  //
  // The form has virtual credential fields (credentials.id_0, credentials.id_1, etc.) but only
  // ONE is visible at a time based on storage_type. These are UI-only fields - not data fields.
  //
  // The credential type allowed is determined by storage_type:
  // - storage_type="snowflake" → only Snowflake credentials allowed → credentials.id_0 shown
  // - storage_type="redshift" → only Redshift credentials allowed → credentials.id_1 shown
  //
  // FLOW:
  //
  // CREATE MODE:
  // 1. User selects storage_type = "snowflake"
  // 2. Form shows credentials.id_0 field (Snowflake credential dropdown)
  // 3. User selects credential → templatizedScriptConfig["credentials.id_0"] = 123
  // 4. This function extracts 123 from the field matching storage_type
  // 5. Sets source.data_credentials_id = 123 (root level)
  // 6. Deletes credentials.id_0 from config (it shouldn't be in script_config.parameters)
  //
  // EDIT MODE (storage_type is DISABLED - see ConfigureTemplatizedScript.js:386-406):
  // 1. Source has storage_type="snowflake", data_credentials_id=123
  // 2. Form loads with credentials.id_0 = 123, storage_type locked
  // 3. User can keep 123 or select different Snowflake credential 456
  // 4. This function extracts credential from credentials.id_0
  // 5. Sets source.data_credentials_id = 123 or 456 (user's choice preserved)
  // 6. Deletes credentials.id_0 from config
  //
  // IMPORTANT: The credential fields (credentials.id_X) are ONLY for determining which credential
  // dropdown to show in the UI. The actual credential is stored at source.data_credentials_id.
  //
  // NOTE: actions/sources/index.js:583 has special handling for sql_runner to NOT override
  // data_credentials_id with selectedCredentialId, ensuring we use the storage_type-matched value.
  // =============================================================================

  // Extract credential ID from the form field that matches current storage_type
  const storageType = config && config['parameters.storage_type'];
  const credentialFields = configFields.filter((f) => f.type === 'script_credential');

  let credentialFound = false;
  credentialFields.forEach((field) => {
    const credId = templatizedScriptConfig[field.name];

    // Find the credential field that matches storage_type and has a value
    // (Only one field will be visible and populated based on storage_type)
    if (!credentialFound && credId && storageType && field.dependsOn) {
      const { dependsOn } = field;
      let matches = false;

      // Handle complex dependency structures: anyOf (OR) or allOf (AND)
      // Check if this credential field should be visible for the current storage_type
      if (dependsOn.anyOf || dependsOn.allOf) {
        const dependencies = dependsOn.anyOf || dependsOn.allOf;
        matches = dependencies.some((dep) => {
          if (dep.name === 'parameters.storage_type') {
            // Check array of allowed values (e.g. ["redshift", "postgres"])
            if (dep.oneOfValues) {
              return dep.oneOfValues.includes(storageType);
            }
            // Check single value match
            else if (dep.value !== undefined) {
              return dep.value === storageType;
            }
          }
          return false;
        });
      }
      // Handle simple dependency: { name: "parameters.storage_type", oneOfValues: [...] }
      else if (dependsOn.name === 'parameters.storage_type') {
        if (dependsOn.oneOfValues) {
          matches = dependsOn.oneOfValues.includes(storageType);
        } else if (dependsOn.value !== undefined) {
          matches = dependsOn.value === storageType;
        }
      }

      // If this field matches the storage_type, use its credential ID
      if (matches) {
        source.data_credentials_id = credId;
        credentialFound = true;
      }
    }
  });

  // Omit start.cron from script_config.config; only parameters.script.start.cron is needed there.
  delete config['start.cron'];

  return source;
}

function extractParamFromURL(match, paramName = 'id', castToInteger = true) {
  if (_.has(match, `params.${paramName}`)) {
    const paramValue = match.params[paramName];

    if (!castToInteger) {
      return paramValue;
    }

    const viewResourceIdNum = _.toNumber(paramValue);

    if (_.isFinite(viewResourceIdNum)) {
      return viewResourceIdNum;
    }
  }

  return null;
}

function generateNonce(length = 10) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let nonce = '';

  for (let i = 0; i < length; i++) {
    nonce += _.sample(chars);
  }

  return nonce;
}

function isHCEnabled(orgId, connectionType, isSource = true) {
  return (
    HC_SETTINGS_BY_ORG &&
    HC_SETTINGS_BY_ORG[orgId] &&
    HC_SETTINGS_BY_ORG[orgId][isSource ? 'source' : 'sink'].includes(connectionType)
  );
}

function disableInvalidHCOptions(options, savedValue = null) {
  if (!savedValue) {
    return options;
  }

  const valueAsInt = parseInt(savedValue, 10);

  return _.map(options, (option) => ({
    ...option,
    isDisabled: parseInt(option.value, 10) < valueAsInt,
  }));
}

function replaceMacros(props, parseFn = null) {
  const {
    templateURL,
    config,
    fields = [],
    placeholderPattern = /\$\{([\w.-]+?)\}/g,
    selectedCredential = null,
  } = props;

  // If templateURL is not there, no need to replace macros.
  if (!templateURL) return templateURL;

  const placeholderMatches = [...templateURL.matchAll(placeholderPattern)];

  let updatedValue = _.reduce(
    placeholderMatches,
    (newValue, match) => {
      const placeholder = match[0];
      const propertyName = match[1];

      let placeholderValue;

      if (_.has(config, propertyName)) {
        placeholderValue = config[propertyName];
      } else if (
        selectedCredential &&
        _.has(selectedCredential.credentials_non_secure_data, propertyName)
      ) {
        placeholderValue = selectedCredential.credentials_non_secure_data[propertyName];
      } else if (
        selectedCredential &&
        _.has(selectedCredential, 'template_config') &&
        _.has(selectedCredential.template_config, propertyName)
      ) {
        placeholderValue = selectedCredential.template_config[propertyName];
      }

      /**
       * ParseFn is a callback, if want any alternation on this value
       */
      if (placeholderValue && parseFn) {
        placeholderValue = parseFn(placeholderValue, propertyName);
      }

      //  default value
      if (!placeholderValue) {
        const field = _.find(fields, {
          name: propertyName,
        });
        if (field) {
          placeholderValue = `<${field.display_name}>`;
        }
      }
      if (_.isNil(placeholderValue)) {
        return newValue;
      }
      return newValue.replace(placeholder, placeholderValue);
    },
    templateURL,
  );

  return updatedValue;
}

function replacePlaceholdersGeneric(
  data,
  placeholderValues,
  placeholderPattern = /\$\{([\w.-]+?)\}/g,
) {
  return _.mapValues(data, (value) =>
    _.isString(value)
      ? value.replace(placeholderPattern, (placeholder, propertyName) =>
          _.has(placeholderValues, propertyName) ? placeholderValues[propertyName] : placeholder,
        )
      : value,
  );
}

function shouldAddURLParams(field, values) {
  const { ignoreIf, urlParam } = field;
  if (!urlParam) return false;
  let shouldNotRender = false;

  if (!_.isNil(ignoreIf)) {
    if (_.has(ignoreIf, 'allOf') || _.has(ignoreIf, 'anyOf')) {
      let requiresAll = _.has(ignoreIf, 'allOf') ? true : false;
      //iterate over each value of ignoreIf, and should render if only all checks pass for allOf, and render if any check passes for anyOf

      let conditions = requiresAll === true ? ignoreIf.allOf : ignoreIf.anyOf;
      let shouldRenderChecks = [];
      _.forEach(conditions, (thisCondition, idx) => {
        let thisShouldRender = true;
        if (_.has(values, thisCondition.name)) {
          //could depend on one of many values of ignoreIf.
          let valueOfignoreIf = values[thisCondition.name];
          if (_.has(thisCondition, 'notOneOfValues')) {
            thisShouldRender = !_.includes(thisCondition.notOneOfValues, valueOfignoreIf);
          } else if (_.has(thisCondition, 'oneOfValues')) {
            thisShouldRender = _.includes(thisCondition.oneOfValues, valueOfignoreIf);
          } else {
            thisShouldRender = valueOfignoreIf === thisCondition.value;
          }
        } else {
          thisShouldRender = !!thisCondition.ignoreIfEmpty;
        }
        shouldRenderChecks.push(thisShouldRender);
      });

      _.forEach(shouldRenderChecks, (thisCheck) => {
        if (requiresAll === true) {
          if (thisCheck === false) {
            shouldNotRender = false;
            return false;
          } else {
            shouldNotRender = true;
          }
        } else {
          if (thisCheck === true) {
            shouldNotRender = true;
            return false;
          } else {
            shouldNotRender = false;
          }
        }
      });
    } else {
      if (_.has(values, ignoreIf.name)) {
        //could depend on one of many values of ignoreIf.
        let valueOfignoreIf = values[ignoreIf.name];
        if (_.has(ignoreIf, 'notOneOfValues')) {
          shouldNotRender = !_.includes(ignoreIf.notOneOfValues, valueOfignoreIf);
        } else if (_.has(ignoreIf, 'oneOfValues')) {
          shouldNotRender = _.includes(ignoreIf.oneOfValues, valueOfignoreIf);
        } else {
          shouldNotRender = valueOfignoreIf === ignoreIf.value;
        }
      } else {
        shouldNotRender = ignoreIf.ignoreIfEmpty;
      }
    }
  }

  return !shouldNotRender;
}

function getMainPage() {
  let redirectedRoute = '';
  let location = window.location.pathname.split('/');
  let lastEle = location.pop();

  // if editing/viewing a resource, go to the resource listing
  if (lastEle === 'edit') {
    location.pop();
    redirectedRoute = location.join('/');
  } else if (/^\d+$/.test(lastEle)) {
    redirectedRoute = location.join('/');
  }

  // else, go to whichever page was open
  else {
    redirectedRoute = window.location.pathname;
  }

  return redirectedRoute;
}

/**
 *
 * @param [] fields Array of fields
 * @param {} config Sink or Source Config
 * @returns {} Sink or Source config
 */
function omitInvisibleFieldData(fields = [], config = {}) {
  if (!_.isArray(fields)) return config;
  if (!config || _.isEmpty(config)) return config;

  // omit fields which shouldn't be rendered
  const omitFieldNames = fields
    .filter((field) => !shouldRenderField(field, config))
    .map((field) => field.name);

  const newConfig = _.omit(config, omitFieldNames);

  return newConfig;
}

/**
 * remove the disabled rules from optionsOrder array based on the flow_type settled on disabled property
 *
 * e.g:
 *
 * {
 *   miscGroup: {
 *     label: 'label',
 *     disabled: {
 *       options: {
 *         getNexsetData: {
 *           flow_type: ['in_memory']
 *         }
 *       }
 *     },
 *     optionsOrder: ['constVal', 'eqAttr', 'remove', 'hoist', 'getNexsetData']
 *   }
 * }
 *
 */
function filterGroupedRulesByDataset(dataSet, groupRules) {
  const flowType = dataSet?.flow_type ?? 'streaming';
  const groupRulesEntries = Object.entries(groupRules); // {[string, value][]}

  return groupRulesEntries.reduce((map, [ruleKey, value]) => {
    const disabledOptions = value?.disabled?.options || {};
    const entries = Object.entries(disabledOptions);

    const disabledOptionsKey = entries.map(([disabledOptionKey, disabledOptionValue]) => {
      return disabledOptionValue.flow_type.includes(flowType) ? disabledOptionKey : '';
    });

    const filter = (i) => !disabledOptionsKey.includes(i);
    value.optionsOrder = value.optionsOrder.filter(filter);

    return {
      ...map,
      [ruleKey]: value,
    };
  }, {});
}

function wait(time) {
  return new Promise((resolve) => setTimeout(resolve, time));
}

function toTitleCase(str) {
  return str.replace(
    /\w\S*/g,
    (text) => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase(),
  );
}

function formatList(list, joinText, lastJoinText) {
  const last = list.pop();
  return list.length === 0 ? last : [list.join(joinText), last].join(lastJoinText);
}

/**
 * Returns a date object for the current time.
 * @param {string} interval - The interval to use for the cron expression.
 * @returns {Object} - The current date and time.
 */
function getCurrentDateAndTime(interval) {
  const now = new Date();
  const hours = now.getHours();
  const minutes = Math.ceil(now.getMinutes() / 5) * 5;
  const hh = padStart(now.getHours() % 12 || 12, 2, '0');
  const mm = padStart(Math.min(55, minutes), 2, '0');
  const aa = now.getHours() < 12 ? 'AM' : 'PM';
  const dayOfWeek = String(now.getDay());
  const dayOfMonth = String(now.getDate());
  const timestamp = new Date(
    now.getFullYear(),
    now.getMonth(),
    dayOfMonth,
    hours,
    minutes,
  ).getTime();
  const n = '2';

  return {
    interval,
    hh,
    mm,
    aa,
    dayOfWeek,
    dayOfMonth,
    timestamp,
    n,
  };
}

var UIUtils = {
  formatList,
  filterGroupedRulesByDataset,
  filterSchemaAttributes,
  getAttrDisplayName,
  getLocationFromSourceConfig,
  parsePartsFromURL,
  toPluralFormat,
  renderAndJoin,
  getDatasetSharersCount,
  getSinkMappingFromAttributes,
  getMetadataSamplesByAttrId,
  getSamplesByAttrId,
  shouldHideField,
  shouldRenderField,
  configWithMissingDefaults,
  reduceConfig,
  unflattenTree,
  fillConditionalDefault,
  castToFieldType,
  tagsToOps,
  opsToTags,
  getCron,
  getCurrentDateAndTime,
  parseCron,
  stringToParagraphs,
  getWebhookSampleRequest,
  isSourceAutoGenerated,
  isFieldInSourceLocation,
  isFieldInSinkLocation,
  disallowSourceCopy,
  intersperse,
  formatsWithColumns,
  headerlessFormats,
  getSectionsFromFields,
  getSectionElementId,
  getActiveSections,
  buildMapping,
  getMappingForBuild,
  buildTemplatizedAPIConfigFromSource,
  buildSourceFromTemplatizedAPIConfig,
  buildTemplatizedScriptConfigFromSource,
  buildTemplatizedScriptConfigFromSqlRunnerSource,
  buildSourceFromTemplatizedScriptConfig,
  buildSqlRunnerSourceFromTemplatizedScriptConfig,
  extractParamFromURL,
  generateNonce,
  isHCEnabled,
  disableInvalidHCOptions,
  replaceMacros,
  replacePlaceholdersGeneric,
  shouldAddURLParams,
  getMainPage,
  omitInvisibleFieldData,
  dependsOnField,
  toTitleCase,
  wait,
};

export default UIUtils;
